/**
 * Devices view — one equipment-type group inside a location card (#999 Story 3.5).
 *
 * Reuses the same read-only step configuration table as each repeatable step
 * (via LocationViewStepList). LOCATION column omitted — the card title is the
 * location.
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, spacing } from '../../../theme';
import { Battery, Droplet, Server, Snowflake, Zap } from '../../icons';
import { LocationViewStepList } from '../LocationViewStepList';

import type { DeviceSourceUnit } from '../../../utils/deviceRowMapper';
import type { DeviceStepId } from '../../../utils/devicesViewModel';
import type { DeviceTypeGroup as DeviceTypeGroupModel } from '../../../utils/devicesViewModel';

type IconComponent = React.ComponentType<{ size?: number; color?: string }>;

const TYPE_ICON: Record<DeviceStepId, IconComponent> = {
  'rack-power': Server,
  'small-ups': Battery,
  'large-ups': Battery,
  'power-distribution': Zap,
  'cooling-readiness': Snowflake,
  'liquid-cooling': Droplet,
};

interface DeviceTypeGroupProps {
  group: DeviceTypeGroupModel;
  testID?: string;
  onUnitPress?: (stepId: DeviceStepId, unit: DeviceSourceUnit) => void;
  onReorderUnits?: (
    stepId: DeviceStepId,
    visibleUnits: readonly DeviceSourceUnit[],
    fromIndex: number,
    toIndex: number,
  ) => void;
  /** When false for a step, hide reorder handles (session not ready). */
  canReorderStep?: (stepId: DeviceStepId) => boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
}

export const DeviceTypeGroup: React.FC<DeviceTypeGroupProps> = ({
  group,
  testID,
  onUnitPress,
  onReorderUnits,
  canReorderStep,
  resolveLocationTagName,
}) => {
  const TypeIcon = TYPE_ICON[group.stepId];

  return (
    <View style={styles.group} testID={testID}>
      <View style={styles.heading}>
        <View style={styles.headingLeft}>
          {TypeIcon ? <TypeIcon size={14} color={colors.textMuted} /> : null}
          <Text style={styles.headingTitle}>
            {group.stepTitle.toUpperCase()}
          </Text>
        </View>
      </View>

      <View
        style={styles.listWrap}
        testID={testID ? `${testID}-list-wrap` : undefined}
      >
        <LocationViewStepList
          stepId={group.stepId}
          units={group.units}
          testIDPrefix={testID ?? 'pd-devices-step'}
          resolveLocationTagName={resolveLocationTagName}
          onUnitPress={
            onUnitPress
              ? (unit: DeviceSourceUnit) => onUnitPress(group.stepId, unit)
              : undefined
          }
          onReorderUnits={
            onReorderUnits && (canReorderStep?.(group.stepId) ?? true)
              ? (fromIndex, toIndex) =>
                  onReorderUnits(group.stepId, group.units, fromIndex, toIndex)
              : undefined
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  group: {
    // 12px separator between equipment-type groups (Chapman). This is the only
    // vertical gap between types: the heading no longer adds top padding and the
    // embedded table's trailing margin is cancelled by `listWrap` below.
    marginTop: spacing.md,
  },
  listWrap: {
    // The shared step table section carries `marginBottom: spacing.lg` (16px) to
    // separate its own sub-sections. In the Devices card the group spacing is
    // owned by `group.marginTop`, so cancel that trailing margin here to avoid a
    // ~28px stacked gap between types.
    marginBottom: -spacing.lg,
  },
  heading: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: spacing.sm,
    marginBottom: spacing.xs,
  },
  headingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  headingTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textMuted,
    letterSpacing: 0.7,
  },
});
